import React, { useEffect, useState } from 'react';
import { Card, Icon } from 'semantic-ui-react';
import useTheme from '../../../../hooks/useTheme';
import { useResponsive } from '../../../UI/ResponsiveContext';
import AppText from '../../../UI/Texts/AppTexts';
import { t } from 'i18next';
import { AppPrimaryButton } from '../../../UI/CustomButtons';
import { SizedBox } from '../../../UI/Sizes';
import { AppColumn, AppRow } from '../../../UI/Layouts';
import AppPrimaryTextfield from '../../../UI/TextInput';
import PopupDropdown from '../../../UI/Dropdown';
import { connect, useSelector } from 'react-redux';
import { formatEndDate, formatStartDate, generateLandingSearchUrl, getLocale, getLocationNameFromKey, isoDate } from '../../../../utils/helperFunctions';
import DateTimePicker from '../../../UI/DatePicker/DateTimePicker';

const AvailableJobsSearchCard = ({ searchButtonRef }) => {
  const theme = useTheme();
  const { isMobile, size, isTablet } = useResponsive();

  const isMobileView = isMobile || isTablet;


  const core = useSelector((state) => state?.core);
  const constants = core?.constants;

  const availableJobsData = useSelector((state) => state?.jobs?.availableJobs);


  const searchAvailableRoleData = availableJobsData.queryData?.role;
  const searchAvailableLocationData = availableJobsData.queryData?.location;

  const [queryData, setQueryData] = useState(availableJobsData?.queryData ? {
    location: searchAvailableLocationData?.key,
    role: searchAvailableRoleData?.roleId,
  } : null);


  const coreConstants = Object.freeze(core?.constants ?? {});


  const jobLocations = (coreConstants.appLocations ?? []).sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  useEffect(() => {
    if (availableJobsData?.queryData) {
      setQueryData({
        location: availableJobsData?.queryData?.location?.key,
        role: availableJobsData?.queryData?.role?.roleId,
      });
    }
  }, [availableJobsData?.queryData]);

  const newSearchedLocations = jobLocations.map((location) => {
    return {
      key: location.key,
      value: location.key,
      text: location.name,
    }
  });


  const onDatesUpdate = (startDate, endDate) => {
    setQueryData({
      ...queryData,
      fromDate: startDate,
      endDate: endDate,
    });
  };



  const headerText = (() => {
    if (searchAvailableRoleData && searchAvailableLocationData) {
      return t('availablejobs.role_location.title', {
        role: searchAvailableRoleData?.text[getLocale()]?.toLowerCase(),
        location: searchAvailableLocationData?.name,
      });
    } else if (searchAvailableRoleData) {
      return t('availablejobs.role.title', {
        role: searchAvailableRoleData?.text[getLocale()]?.toLowerCase(),
      });
    } else if (searchAvailableLocationData) {
      return t('availablejobs.location.title', {
        location: searchAvailableLocationData?.name,
      });
    } else {
      return t('availablejobs.title');
    }
  })()

  const dateTextFieldWidth = isMobileView ? (isMobile ? '100%' : `${((size.width) / 2.55)}px`) : '100%';

  const cardDetails = (<AppColumn >
    <AppText.HeadingSmall fontWeight={700} margins={{
      marginTop: '0px',
      marginBottom: '0px',
    }} use={'h1'} >{headerText}</AppText.HeadingSmall>

    <SizedBox height={theme.spacing.elementPaddingSmall} />

    <AppText.Caption use='strong' color={theme.colors.grey}>{t('availablejobs.description')}</AppText.Caption>

    <SizedBox height={theme.spacing.contentElementPadding} />


    <PopupDropdown
      options={newSearchedLocations}
      position={"bottom left"}
      basic={true}
      onChange={(location) => {
        setQueryData({
          ...queryData,
          location: location.value,
        });
      }}
      newSearchValue={queryData?.location}
      customPlaceholderWithCallback={(isOpen, setOpen) => {

        return (
          <AppPrimaryTextfield
            labelText={t('location')}
            hintText={t('anywhere_in_sweden')}
            material
            value={getLocationNameFromKey(coreConstants, queryData?.location ?? '')}
            onChange={(value => {
              setQueryData({
                ...queryData,
                location: value,
              });

              setOpen();
            })}
          />
        )
      }}

    />


    <SizedBox height={theme.spacing.elementPaddingSmall} />

    <AppRow style={{
      width: '100%',
    }}
      mainAxisAlignment={'space-between'} crossAxisAlignment={'stretch'}
    >

      <DateTimePicker
        key={"start_date"}
        selectRange={false}
        initialDate={queryData?.fromDate}
        onChange={(date) => {
          onDatesUpdate(date, undefined);
        }}
        childBuilder={(dates, toggleCalendar) => {

          return (
            <AppPrimaryTextfield
              labelText={t('start')}
              hintText={t('select_date')}
              readOnly
              material
              fluid
              style={{
                width: dateTextFieldWidth,
              }}
              value={formatStartDate(queryData?.fromDate, {
                full: true
              })}
              onClick={toggleCalendar}
              maxLine={1}
            />
          );
        }}
      />
      <SizedBox width={theme.spacing.elementPaddingSmall} />
      <DateTimePicker
        key={"end_date"}
        selectRange={true}
        initialDate={queryData?.fromDate}
        onChange={(date) => {
          onDatesUpdate(
            queryData?.fromDate,
            date[1]
          );
        }}
        childBuilder={(dates, toggleCalendar) => {
          return (
            <AppPrimaryTextfield
              labelText={t('end')}
              hintText={t('select_date')}
              fluid
              value={formatEndDate(queryData?.endDate, {
                full: true
              })}
              style={{
                width: dateTextFieldWidth,
              }}
              onClick={toggleCalendar}
              readOnly
              material
              maxLine={1}
            />
          );
        }}
      />
    </AppRow>
    <SizedBox height={theme.spacing.contentElementPadding} />

    <div ref={searchButtonRef} style={{
      width: '100%',
    }}>
      <AppPrimaryButton
        size='big'
        href={`/${generateLandingSearchUrl({
          constants: constants,
          locations: queryData?.location ? [queryData?.location] : [],
          workroles: queryData?.role && [queryData?.role],
          fromDate: isoDate({ dateString: queryData?.fromDate }),
          toDate: isoDate({ dateString: queryData?.endDate }),
        })}`}
        target="_blank"
        style={{
          borderRadius: theme.radius.button,
          width: '100%',
          height: theme.buttonHeight,
        }}>
        <AppRow mainAxisAlignment={'center'} crossAxisAlignment={'center'} style={{
          marginTop: '0.2rem',
        }}>
          <Icon name="search" style={{
            color: theme.colors.white,
            paddingRight: theme.spacing.elementPadding,
          }} />

          {t('search')}
        </AppRow>
      </AppPrimaryButton>
    </div>
  </AppColumn>)

  if (isMobileView) {
    return <div>
      <SizedBox height={theme.spacing.elementPadding} />
      {cardDetails}
    </div>;
  }


  return (
    <Card
      style={{
        padding: theme.spacing.contentElementPadding,
        borderRadius: theme.radius.default,
        boxShadow: "0 10px 15px rgba(0, 0, 0, 0.2)",
        border: "1px solid rgba(0, 0, 0, 0.1)",
        width: '36%',
        position: 'absolute',
        minHeight: 380,
        top: '10%',
        left: '0%',
        zIndex: 1000,
      }}
    >
      {cardDetails}
    </Card>
  );
}

export default connect(null, null)(AvailableJobsSearchCard);